import { useState, useEffect, useMemo, useCallback } from 'react';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { Download, X } from 'lucide-react';
import { Copy } from 'lucide-react';
import { CsvEditor } from 'src/shared/components/thread-store/csv/CsvEditor';
import { Separator } from 'src/shared/components/ui/separator';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { useToast } from 'src/shared/components/ui/use-toast';
import {
  ApprovalData,
  PUSH_CSV_FILE_TO_OUTLOOK_APP,
  PushCsvFileToOutlookAppData,
  ThreadStore,
} from 'src/features/flows/types/threadStore.types';
import { useThreadStores } from 'src/features/flows/queries/useThreadStores';
import { ButtonWithActions } from 'src/shared/components/ui/buttons/button-with-actions';
import SaveButton from 'src/shared/components/ui/buttons/save-button';
import { useMutateThreadStore } from 'src/features/flows/queries/useMutateThreadStore';
import { Loading } from 'src/shared/components/ui/loaders/loading';
import EmptyStateCard from 'src/shared/components/ui/cards/empty-state-card';
import { Button } from 'src/shared/components/ui/buttons/button';

interface CsvEditorModalProps {
  // Required for fetching data
  threadStoreId: string;
  integrationId: string;
  threadId: string;
  // Optional - if provided, indicates we're editing an approval value
  valueToApproveId?: string;
  // Optional - if provided, skips data fetching
  file?: string;
  title?: string;
  filename?: string;
  containsHeader?: boolean;
  editable?: boolean;
  // Callback when modal should close
  onClose: () => void;
  // Optional callback after successful save
  onSave?: () => void;
  // Whether to show download button (may be disabled on some platforms)
  showDownload?: boolean;
}

interface FileData {
  file: string;
  filename?: string;
  containsHeader: boolean;
  title: string;
}

/**
 * Platform-agnostic CSV editor modal content.
 * Can be used inside any modal/sheet/dialog component.
 * Does not depend on Office.js - uses callbacks for close/save actions.
 */
export function CsvEditorModal({
  threadStoreId,
  integrationId,
  threadId,
  valueToApproveId,
  file: propFile,
  title: propTitle,
  filename: propFilename,
  containsHeader: propContainsHeader,
  editable: propEditable,
  onClose,
  onSave,
  showDownload = true,
}: CsvEditorModalProps) {
  const { orgFetch } = useOrg();
  const { toast } = useToast();
  const { updateThreadStore } = useMutateThreadStore();

  // Always fetch threadStore when we need to approve (to get flowId and approvalId)
  // Also fetch if we don't have file data from props
  const shouldFetch = !propFile || !!valueToApproveId;
  const { data: threadStoresData, isPending } = useThreadStores(
    shouldFetch ? integrationId : undefined,
    shouldFetch ? threadId : undefined
  );

  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [fileData, setFileData] = useState<FileData | undefined>(
    propFile
      ? {
          file: propFile,
          filename: propFilename,
          containsHeader: propContainsHeader ?? true,
          title: propTitle ?? 'CSV Data',
        }
      : undefined
  );
  const [isSaving, setIsSaving] = useState(false);

  // Find the thread store from fetched data
  const threadStore = useMemo<ThreadStore | undefined>(() => {
    if (!shouldFetch) return undefined;
    return threadStoresData?.find((t) => t.id === threadStoreId);
  }, [threadStoresData, threadStoreId, shouldFetch]);

  // Determine if editable based on props or thread store state
  const editable = useMemo(() => {
    if (propEditable !== undefined) return propEditable;
    if (!valueToApproveId) return false;
    if (!threadStore) return false;
    return threadStore.type === 'approval' && ['pending', 'failed'].includes(threadStore.data.status);
  }, [propEditable, valueToApproveId, threadStore]);

  // Extract file data from thread store if needed
  useEffect(() => {
    if (fileData || !threadStore) return;

    if (threadStore.type === PUSH_CSV_FILE_TO_OUTLOOK_APP) {
      setFileData(threadStore.data as PushCsvFileToOutlookAppData);
    } else if (threadStore.type === 'approval' && valueToApproveId) {
      const record = (threadStore.data as ApprovalData).valuesToApprove[valueToApproveId];
      if (record) {
        setFileData({
          file: record.value,
          filename: 'approval-value.csv',
          containsHeader: record.containsHeader ?? true,
          title: record.label || 'CSV Data',
        });
      }
    }
  }, [threadStore, valueToApproveId, fileData]);

  // Parse CSV when file data changes
  useEffect(() => {
    if (!fileData?.file || typeof fileData.file !== 'string') {
      setHeaders([]);
      setDataRows([]);
      setDelimiter(',');
      return;
    }

    try {
      const parsed = Papa.parse<string[]>(fileData.file, {
        header: false,
        skipEmptyLines: true,
      });

      const csvDelimiter = parsed?.meta?.delimiter || ',';
      setDelimiter(csvDelimiter);

      const allRows = parsed.data;

      if (allRows.length > 0) {
        if (fileData.containsHeader) {
          setHeaders(allRows[0]);
          setDataRows(allRows.slice(1));
        } else {
          setHeaders([]);
          setDataRows(allRows);
        }
      } else {
        setHeaders([]);
        setDataRows([]);
        setDelimiter(',');
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      setHeaders([]);
      setDataRows([]);
      setDelimiter(',');
    }
  }, [fileData?.file, fileData?.containsHeader]);

  // Reconstruct CSV from edited data using Papa.unparse for proper escaping
  const reconstructCsv = useCallback((): string => {
    const allRows = fileData?.containsHeader ? [headers, ...dataRows] : dataRows;
    return Papa.unparse(allRows, {
      delimiter: delimiter,
    });
  }, [headers, dataRows, delimiter, fileData?.containsHeader]);

  const handleDownload = useCallback(() => {
    if (!fileData) return;
    const csvContent = reconstructCsv();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileData.filename ?? `${fileData.title}.csv`);
  }, [fileData, reconstructCsv]);

  const handleCopy = useCallback(async () => {
    if (!fileData) return;

    // Convert CSV to TSV before copying
    let tsvValue: string;
    try {
      const parsed = Papa.parse<string[]>(fileData.file, {
        header: false,
        skipEmptyLines: false,
      });
      tsvValue = parsed.data.map((row) => row.join('\t')).join('\n');
    } catch (error) {
      console.error('Error converting CSV to TSV:', error);
      tsvValue = fileData.file; // Fallback to original
    }

    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(tsvValue);
        toast({
          title: 'Copied to clipboard',
          variant: 'success',
        });
        return;
      }
    } catch (err) {
      // Clipboard API failed (e.g., permissions policy in iframe)
      console.warn('Clipboard API failed, trying fallback:', err);
    }

    // Fallback: use older execCommand method
    try {
      const textArea = document.createElement('textarea');
      textArea.value = tsvValue;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        toast({
          title: 'Copied to clipboard',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Failed to copy',
          description: 'Please copy manually',
          variant: 'error',
        });
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      toast({
        title: 'Failed to copy',
        description: 'Please copy manually',
        variant: 'error',
      });
    }
  }, [fileData, toast]);

  const handleSave = useCallback(async () => {
    if (!valueToApproveId) return;

    try {
      setIsSaving(true);
      await updateThreadStore.mutateAsync({
        threadStoreId,
        valuesToApprove: { [valueToApproveId]: reconstructCsv() },
      });
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Error saving:', err);
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [valueToApproveId, threadStoreId, reconstructCsv, updateThreadStore, onSave, onClose, toast]);

  const handleApprove = useCallback(async () => {
    if (!threadStore || threadStore.type !== 'approval') return;

    try {
      const res = await orgFetch<any>(
        `/api/flow/v2/${threadStore.flowId}/approval/${threadStore.data.approvalId}?result=approved`,
        {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        throw new Error('Failed to approve');
      }
    } catch (err) {
      console.error('Error approving:', err);
      toast({
        title: 'Error',
        description: 'Failed to approve',
        variant: 'error',
      });
      throw err;
    }
  }, [threadStore, orgFetch, toast]);

  const handleSaveAndApprove = useCallback(async () => {
    if (!valueToApproveId) return;

    try {
      setIsSaving(true);
      await updateThreadStore.mutateAsync({
        threadStoreId,
        valuesToApprove: { [valueToApproveId]: reconstructCsv() },
      });
      await handleApprove();
      onSave?.();
      onClose();
    } catch (err) {
      console.error('Error saving and approving:', err);
    } finally {
      setIsSaving(false);
    }
  }, [valueToApproveId, threadStoreId, reconstructCsv, updateThreadStore, handleApprove, onSave, onClose]);

  // Loading state
  if (shouldFetch && isPending) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8">
        <Loading />
      </div>
    );
  }

  // No data state
  if (!fileData || dataRows.length === 0) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center p-8">
          <EmptyStateCard
            icon="FileSpreadsheet"
            title="No data"
            description="This file does not contain any data to show."
          />
        </div>
        <Separator />
        <div className="flex justify-end p-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white flex flex-col">
      {/* Header */}
      <div className="flex p-3 items-center shrink-0">
        <div className="flex gap-2 items-center">
          <span className="font-semibold text-sm">{fileData.title}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 hover:text-neutral-800 text-neutral-400 transition-all"
          >
            <Copy size={12} />
          </button>
        </div>
        <div className="flex-grow" />
        {showDownload && (
          <button
            type="button"
            onClick={handleDownload}
            className="mr-3 text-typography-grey hover:underline underline-offset-2 hover:text-typography-black transition-all text-xs font-light flex items-center gap-1.5 cursor-pointer"
          >
            <Download size={12} />
            Download
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mr-1 text-typography-grey hover:underline underline-offset-2 hover:text-typography-black transition-all text-xs font-light flex items-center gap-1.5 cursor-pointer"
        >
          <X size={12} />
          Close
        </button>
      </div>

      <Separator className="shrink-0" />

      {/* Content */}
      <div className="flex-1 w-full bg-white p-4 overflow-auto">
        <CsvEditor headers={headers} rows={dataRows} editable={editable} onDataChange={setDataRows} />
      </div>

      {/* Footer with actions */}
      {editable && valueToApproveId && (
        <>
          <Separator className="shrink-0" />
          <div className="flex w-full justify-end px-5 py-2 gap-2 shrink-0">
            <SaveButton variant="outline" loading={false} onClick={onClose} disabled={isSaving}>
              Cancel
            </SaveButton>
            <ButtonWithActions
              disabled={isSaving}
              actions={[
                {
                  label: 'Save',
                  onClick: handleSave,
                },
              ]}
              onMainAction={handleSaveAndApprove}
            >
              {isSaving ? 'Saving...' : 'Save and approve'}
            </ButtonWithActions>
          </div>
        </>
      )}
    </div>
  );
}

export default CsvEditorModal;
